import { useEffect, useState } from "react";

import { Models, ShopDisplayType } from "api/types";
import { Icon } from "atoms/icon/Icon";
import RepeatMD from "atoms/icon/icons/RepeatCash.svg";
import { TextButton } from "atoms/text-button/TextButton";
import { TextField } from "atoms/text-field/TextField";
import { formatCurrency, formatNoCurrency } from "toolbox/Money";
import { calculateRepeatCashEligibleAmount } from "utils/functions";

type ApplyRepeatMDCashProps = {
  repeatCashBalance: number;
  repeatCashOnCart: number;
  onApply?: (amount: string) => void;
  isApplied: boolean;
  cartDetails?: Models<"ShopDisplayOrderResult">;
};

export const ApplyRepeatMDCash = ({
  repeatCashBalance,
  repeatCashOnCart,
  onApply,
  isApplied,
  cartDetails,
}: ApplyRepeatMDCashProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const eligibleAmount = cartDetails
    ? calculateRepeatCashEligibleAmount(cartDetails)
    : 0;
  const maxApplicableAmount = Math.min(repeatCashBalance, eligibleAmount);
  const [repeatCashSetter, setRepeatCashSetter] = useState({
    amount: formatNoCurrency(
      repeatCashOnCart || Math.min(repeatCashBalance, maxApplicableAmount) || 0
    ),
    applying: false,
  });

  const handleApply = () => {
    setRepeatCashSetter({
      amount: repeatCashSetter.amount,
      applying: true,
    });
    onApply?.(repeatCashSetter.amount);
  };

  const handleApplyAll = () => {
    const fullAmountToApply = formatNoCurrency(maxApplicableAmount || 0);
    setRepeatCashSetter({
      amount: fullAmountToApply,
      applying: true,
    });
    onApply?.(fullAmountToApply);
  };

  useEffect(() => {
    // close the accordion if cash changes
    if (repeatCashSetter.applying) {
      setIsExpanded(false);
      setRepeatCashSetter((prev) => ({
        ...prev,
        applying: false,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repeatCashOnCart, repeatCashBalance]);

  const validateAmount = (amount: string) => {
    const amountToApply = Number(amount);
    const maxValue = 999999;

    if (Number.isNaN(amountToApply) || amount === "") {
      return { isValid: false, message: "Invalid amount" };
    }

    if (amountToApply < 0) {
      return { isValid: false, message: "Amount must be positive" };
    }

    if (amountToApply > maxValue) {
      return { isValid: false, message: "Amount cannot exceed $999,999.00" };
    }

    const decimalPlaces = (amount.split(".")[1] || "").length;
    if (decimalPlaces > 2) {
      return {
        isValid: false,
        message: "Amount can have maximum 2 decimal places",
      };
    }

    if (amountToApply * 100 > repeatCashBalance) {
      return { isValid: false, message: "Amount exceeds available cash" };
    }

    if (amountToApply * 100 > maxApplicableAmount) {
      const hasGiftCards = cartDetails?.shopDisplayOrderLineItems?.some(
        (item) => item.displayType === ShopDisplayType.GiftCard
      );

      if (hasGiftCards) {
        return {
          isValid: false,
          message:
            "RepeatCash cannot be applied to gift cards. Maximum eligible amount is " +
            formatCurrency(maxApplicableAmount),
        };
      }

      return {
        isValid: false,
        message:
          "Amount exceeds maximum eligible amount: " +
          formatCurrency(maxApplicableAmount),
      };
    }

    return { isValid: true, message: "" };
  };

  const validation = validateAmount(repeatCashSetter.amount);

  const isAppliedMessage = !isApplied
    ? `You have ${formatCurrency(repeatCashBalance)} available`
    : `${formatCurrency(repeatCashOnCart)} applied`;

  return (
    <div className="w-full rounded border border-light-grey">
      <div className="flex h-16 w-full cursor-pointer items-center justify-between py-3 pl-3 pr-3">
        <div className="flex items-center gap-0.5">
          <Icon svg={RepeatMD} color="primaryText" size="xsmall" />
          <div className="text-bold2">Cash:</div>
          <div className="text-body2">{isAppliedMessage}</div>
        </div>
        <div className="flex items-center gap-2">
          {isApplied && (
            <>
              <TextButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded((prev) => !prev);
                  setRepeatCashSetter({
                    amount: "0",
                    applying: false,
                  });
                }}
                xsText
              >
                {isExpanded ? "CANCEL" : "EDIT"}
              </TextButton>
              <TextButton
                onClick={(e) => {
                  e.stopPropagation();
                  setIsExpanded(false);
                  onApply?.("0");
                }}
                xsText
              >
                REMOVE
              </TextButton>
            </>
          )}
          {!isApplied && (
            <TextButton
              onClick={(e) => {
                e.stopPropagation();
                handleApplyAll();
              }}
              xsText
            >
              APPLY
            </TextButton>
          )}
        </div>
      </div>

      {isExpanded && (
        <div className="w-full border-t border-light-grey px-3 pb-3">
          <div className="flex items-center gap-2 pt-3">
            <div className="flex-1">
              <TextField
                label="Amount $"
                placeholder="0.00"
                className="h-10"
                type="decimal"
                value={repeatCashSetter.amount.toString()}
                invalid={!validation.isValid}
                onChange={(e) => {
                  setRepeatCashSetter({
                    amount: e,
                    applying: false,
                  });
                }}
              />
              <p className="mt-1 text-xs text-secondary">
                {validation.message}
              </p>
            </div>
            <TextButton
              onClick={handleApply}
              xsText
              disabled={!validation.isValid}
            >
              APPLY
            </TextButton>
          </div>
        </div>
      )}
    </div>
  );
};
